import React, { useState, useEffect, useCallback } from 'react';
import { I18n } from '@aws-amplify/core';
import { Form, message, Progress, Row } from 'antd';

import { useResponsive } from './hooks/useResponsive';
import { SurveyContent } from './components/SurveyContent';
import { usePsychosocialAvaliationContext } from '../context';
import { filterQuestionsByType } from './utils/questionHelpers';
import { ModalSaveAnswers } from './components/ModalSaveAnswers';
import { useSurveyPagination } from './hooks/useSurveyPagination';
import { ModalFinishSurvey } from './components/ModalFinishSurvey';
import { PaginationControls } from './components/PaginationControls';
import { useCreateSurveyAnswer } from '@/views/PsychosocialReport/hooks/useCreateSurveyAnswer';
import { useCreateEmployeeSurveyResult } from '@/views/PsychosocialReport/hooks/useCreateEmployeeSurveyResult';
import {
	StyledProgressContainer,
	StyledReferenceText,
	QuestionsContainer,
	StyledQuestionText,
	SubmitButton,
	CenteredCol,
	StyledTitle,
	StyledCard,
	SaveButton
} from './styles';
import { SurveySection } from './types';

interface SurveyResponseProps {
	initialFormValues: Record<string, string | number>;
	percentageCompleted: number;
}

type UserAnswer = {
	question_id: string;
	response?: string;
	option_id?: string;
};

export function SurveyResponse({ initialFormValues, percentageCompleted }: SurveyResponseProps) {
	const {
		handleToggleModalFinishSurvey,
		handleToggleModalSaveAnswers,
		isModalFinishSurveyOpen,
		isModalSaveAnswersOpen,
		clearParticipationId,
		completeSurveyStep,
		participationId,
		surveyData
	} = usePsychosocialAvaliationContext();

	const [form] = Form.useForm();
	const { isMobile, questionsPerPage } = useResponsive();
	const { mutateAsync: createSurveyAnswer } = useCreateSurveyAnswer();
	const { mutateAsync: completeSurvey } = useCreateEmployeeSurveyResult();

	const [isNextDisabled, setIsNextDisabled] = useState(true);
	const [formValues, setFormValues] = useState(initialFormValues || {});

	const { radioQuestions, sliderQuestions, workloadQuestions } = filterQuestionsByType(surveyData?.questions);
	const allQuestions = [...radioQuestions, ...sliderQuestions, ...workloadQuestions];

	function getUserAnswers(): UserAnswer[] {
		if (!initialFormValues || Object.keys(initialFormValues).length === 0) return [];
		return Object.entries(initialFormValues)
			.filter(([key, value]) => key.startsWith('question_') && value != null && value !== '')
			.map(([key, value]) => {
				const questionId = key.replace('question_', '');
				const question = allQuestions.find((q) => q.id === questionId);
				if (!question) return null;
				const hasOptions = Boolean(question.options?.length);
				return {
					question_id: questionId,
					...(hasOptions ? { option_id: String(value) } : { response: String(value) })
				};
			})
			.filter(Boolean) as UserAnswer[];
	}

	const pagination = useSurveyPagination({
		workloadQuestions,
		questionsPerPage,
		sliderQuestions,
		radioQuestions,
		isMobile
	});

	const getCurrentQuestionsAnswered = useCallback(() => {
		const values = form.getFieldsValue();
		const currentQuestions = pagination.getCurrentQuestions();
		return currentQuestions.every((question) => {
			const name = `question_${question.id}`;
			const value = values[name];
			return value !== undefined && value !== null && value !== '';
		});
	}, [form, pagination]);

	useEffect(() => {
		const canProceed = getCurrentQuestionsAnswered();
		setIsNextDisabled(!canProceed);
	}, [pagination.currentPage, pagination.currentSection, formValues, getCurrentQuestionsAnswered]);

	function navigateToQuestion(questionId: string) {
		const sections = [radioQuestions, sliderQuestions, workloadQuestions].filter((q) => q.length);
		for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
			const qs = sections[sectionIndex];
			const idx = qs.findIndex((q) => q.id === questionId);
			if (idx >= 0) {
				const page = Math.floor(idx / questionsPerPage) + 1;
				pagination.goToPage(page, sectionIndex);
				return;
			}
		}
	}

	async function saveAnswer(questionId: string, value: string) {
		if (!surveyData?.participation?.survey_id) return;
		const question = allQuestions.find((q) => q.id === questionId);
		const hasOptions = Boolean(question?.options?.length);
		const params = {
			...(hasOptions ? { option_id: value } : { response: value }),
			participation_id: surveyData.participation.id ?? participationId,
			employee_id: surveyData.participation.employee_id ?? null,
			survey_id: surveyData.participation.survey_id,
			question_id: questionId
		};
		await createSurveyAnswer(params);
		setIsNextDisabled(!getCurrentQuestionsAnswered());
	}

	function handleSaveResponses(): void {
		handleToggleModalSaveAnswers();
	}

	async function handleOnFinish(): Promise<void> {
		for (const question of allQuestions) {
			const name = `question_${question.id}`;
			const value = form.getFieldValue(name);
			if (value === undefined || value === null || value === '') {
				message.error(I18n.get('Please fill all fields before finishing the survey.'));
				navigateToQuestion(question.id);
				form.validateFields();
				window.scrollTo(0, 0);
				return;
			}
		}

		isMobile ? handleToggleModalFinishSurvey() : await createEmployeeSurveyResult();
	}

	async function createEmployeeSurveyResult(): Promise<void> {
		if (!surveyData?.participation?.survey_id) return;

		const params = {
			participation_id: surveyData.participation.id ?? participationId,
			employee_id: surveyData.participation.employee_id ?? null,
			survey_id: surveyData.participation.survey_id
		};

		await completeSurvey(params);
		clearParticipationId();
		completeSurveyStep();
	}

	function renderButtonsSection(): React.ReactNode {
		if (isMobile) {
			return (
				<div style={{ marginTop: 24, display: 'flex', gap: 16, justifyContent: 'center' }}>
					<SaveButton onClick={handleSaveResponses} hidden={surveyData?.is_anonymous}>
						{I18n.get('Save answers')}
					</SaveButton>
					{pagination.isLastSection ? (
						<SubmitButton type="button" onClick={handleOnFinish}>
							{I18n.get('Finish')}
						</SubmitButton>
					) : (
						<SubmitButton type="button" onClick={pagination.handleNext} disabled={isNextDisabled}>
							{I18n.get('Next')}
						</SubmitButton>
					)}
				</div>
			);
		}

		return (
			<div style={{ marginTop: 24, display: 'flex', gap: 16, justifyContent: 'center' }}>
				<SubmitButton type="button" onClick={handleOnFinish}>
					{I18n.get('Finish')}
				</SubmitButton>
			</div>
		);
	}

	function renderQuestionDescription(): React.ReactNode {
		if (pagination.currentSection === SurveySection.RADIO) {
			return (
				<StyledQuestionText>
					{I18n.get('Indicate the frequency of the following statements on the scale:')}
				</StyledQuestionText>
			);
		}
		if (pagination.currentSection === SurveySection.SLIDER) {
			return (
				<StyledQuestionText>
					{I18n.get('Evaluate how you classify the following requirements within the scale:')}
				</StyledQuestionText>
			);
		}
		return null;
	}

	function renderReference(): React.ReactNode {
		if (surveyData?.tool.tool_name === 'nasa_tlx') {
			return (
				<StyledReferenceText>
					{I18n.get(
						'MACEDO, Thiago; CABRAL, Eric Lucas dos Santos; CASTRO, Wilkson; CAMILO, Deyse Gillyane Gomes; MEDEIROS, Veder Ralfh Fernandes de. Aplicação das ferramentas NASA-TLX e QVT-Walton para avaliação de percepção de carga mental e qualidade de vida no trabalho: estudo sobre o setor mesa de crédito numa empresa de teleatendimento. In: ENCONTRO NACIONAL DE ENGENHARIA DE PRODUÇÃO, 38., 2018, Maceió. Anais [...]. Maceió: ABEPRO, 2018.'
					)}
				</StyledReferenceText>
			);
		}
		if (surveyData?.tool.tool_name === 'hse_it') {
			return (
				<StyledReferenceText>
					{I18n.get(
						'LUCCA, Sergio Roberto de; SOBRAL, Renata Cristina. Aplicação de instrumento para o diagnóstico dos fatores de risco psicossociais nas organizações. Campinas: Universidade Estadual de Campinas, 2017'
					)}
				</StyledReferenceText>
			);
		}
		return null;
	}

	return (
		<>
			<Form
				form={form}
				preserve={true}
				layout="vertical"
				style={{ marginTop: 24 }}
				initialValues={initialFormValues}
				onValuesChange={(_, allValues) => {
					setFormValues(allValues);
					setIsNextDisabled(!getCurrentQuestionsAnswered());
				}}
			>
				<Row justify="center">
					<CenteredCol span={24}>
						<StyledCard>
							<StyledTitle level={4}>
								{I18n.get('Questionnaire')} {surveyData?.tool.tool_description}
							</StyledTitle>
							<StyledProgressContainer>
								<span>{I18n.get('Filling progress')}</span>
								<Progress
									percent={percentageCompleted}
									format={(percent) => `${percent}%`}
									success={{ percent: 0 }}
								/>
							</StyledProgressContainer>

							{renderQuestionDescription()}

							<QuestionsContainer>
								<SurveyContent
									currentQuestions={pagination.getCurrentQuestions()}
									currentSection={pagination.currentSection}
									workloadQuestions={workloadQuestions}
									saveAnswer={saveAnswer}
									isMobile={isMobile}
								/>
								{!isMobile && renderReference()}
							</QuestionsContainer>

							{!isMobile && (
								<PaginationControls
									handleSaveResponses={handleSaveResponses}
									totalPages={pagination.getTotalPages()}
									currentPage={pagination.currentPage}
									handleNext={pagination.handleNext}
									handleBack={pagination.handleBack}
									goToPage={pagination.goToPage}
									hasNext={pagination.hasNext}
									hasBack={pagination.hasBack}
									isMobile={isMobile}
								/>
							)}
						</StyledCard>
					</CenteredCol>
				</Row>
				{renderButtonsSection()}
			</Form>
			<ModalSaveAnswers isOpen={isModalSaveAnswersOpen} toggleModal={handleToggleModalSaveAnswers} />
			<ModalFinishSurvey
				toggleModal={handleToggleModalFinishSurvey}
				finishSurvey={createEmployeeSurveyResult}
				navigateToQuestion={navigateToQuestion}
				isOpen={isModalFinishSurveyOpen}
				reference={renderReference()}
				answers={getUserAnswers()}
				questions={allQuestions}
			/>
		</>
	);
}
